import { businessDate, financialYear } from "@luckymenu/core";
import { z } from "zod";

/**
 * Bill numbers: `{prefix}{fy}-{seq}`, for example `LM2627-000123`.
 * GST allows at most 16 characters of letters, digits, `-` and `/`, unique per
 * financial year. See "Bill numbers" in ordering-billing-gst.md.
 */

export const BILL_NO_MAX_LENGTH = 16;
/** The sequence is padded to six digits: up to 999,999 bills a year fit. */
export const BILL_SEQ_DIGITS = 6;
/** 16 − 4 (financial year) − 1 (hyphen) − 6 (sequence). */
export const BILL_PREFIX_MAX_LENGTH = BILL_NO_MAX_LENGTH - 4 - 1 - BILL_SEQ_DIGITS;

const ALLOWED = /^[A-Za-z0-9/-]*$/;

/** `restaurant_billing_settings.bill_prefix`: up to 5 letters, digits, `-` or `/`. May be empty. */
export const billPrefixSchema = z
	.string()
	.max(BILL_PREFIX_MAX_LENGTH, `At most ${BILL_PREFIX_MAX_LENGTH} characters`)
	.regex(ALLOWED, "Letters, digits, - and / only");

/** A formatted bill number, as stored and printed. */
export const billNoSchema = z
	.string()
	.min(1)
	.max(BILL_NO_MAX_LENGTH)
	.regex(ALLOWED, "Letters, digits, - and / only");

/** Four digits for the years of a financial year: "2627" is 2026-27. */
export const financialYearSchema = z
	.string()
	.regex(/^\d{4}$/)
	.refine((fy) => (Number(fy.slice(0, 2)) + 1) % 100 === Number(fy.slice(2)), {
		message: "The second year must follow the first",
	});

/**
 * Formats a bill number. Throws if the prefix has characters GST does not
 * allow, the financial year is malformed, the sequence is not a positive
 * integer, or the result would exceed 16 characters.
 */
export function formatBillNo(prefix: string, fy: string, seq: number): string {
	if (!ALLOWED.test(prefix)) throw new RangeError(`Bill prefix has invalid characters: ${prefix}`);
	if (!financialYearSchema.safeParse(fy).success) {
		throw new RangeError(`Not a financial year: ${fy}`);
	}
	if (!Number.isSafeInteger(seq) || seq < 1) {
		throw new RangeError(`Bill sequence must be a positive integer, got ${seq}`);
	}
	const billNo = `${prefix}${fy}-${String(seq).padStart(BILL_SEQ_DIGITS, "0")}`;
	if (billNo.length > BILL_NO_MAX_LENGTH) {
		throw new RangeError(`Bill number ${billNo} is longer than ${BILL_NO_MAX_LENGTH} characters`);
	}
	return billNo;
}

/**
 * The financial year of a bill finalised at `instant`: 1 April to 31 March,
 * taken from the business date. With the default 04:00 day start, a bill at
 * 01:30 on 1 April still belongs to 31 March and the old financial year.
 */
export function billFinancialYear(instant: Date, timeZone?: string, dayStartHour?: number): string {
	return financialYear(businessDate(instant, timeZone, dayStartHour));
}
